import { computeTalkIdFromTalkData } from '../../shared/cid';
import {
  STARTER_TAGS_CONTACT_ID,
  STARTER_TAGS_SOURCE_ID,
  STARTER_TAGS_VERSION,
  detachStarterTagTalk,
  isStarterTagTalk,
  starterTagTalk,
  starterTagsFor,
} from '../../shared/starter-tags';
import { getMyTalks, patchMyTalk } from './my-talks-storage';
import type { KnownPerson } from '../../shared/types';
import type { MyTalkMap } from './my-talks-storage';
import type { UiLanguage } from './ui-translations';
import { settingsStorageAsStorage } from '../services/core-storage';

export const STARTER_CONTACTS_VERSION = 1;

export type StarterPracticeContactId = 'tag-guide' | 'flow-guide' | 'survey-guide' | 'route-guide';

export type StarterPracticeContact = {
  id: StarterPracticeContactId;
  userId: string;
  icon: string;
  talkType: 'tag' | 'flow' | 'survey' | 'route';
  name: string;
  description: string;
  talks: any[];
};

const definitions: Array<{
  id: StarterPracticeContactId;
  userId: string;
  icon: string;
  talkType: StarterPracticeContact['talkType'];
}> = [
  { id: 'tag-guide', userId: 'iinpublic-demo-tag-guide', icon: '🏷️', talkType: 'tag' },
  { id: 'flow-guide', userId: 'iinpublic-demo-flow-guide', icon: '➡️', talkType: 'flow' },
  { id: 'survey-guide', userId: 'iinpublic-demo-survey-guide', icon: '📊', talkType: 'survey' },
  { id: 'route-guide', userId: 'iinpublic-demo-route-guide', icon: '🔀', talkType: 'route' },
];

const stateKey = (userId: string): string => `iinpublic_starter_contacts_v${STARTER_CONTACTS_VERSION}:${userId}`;
// Device-wide opt-out set by the E2E harness (tests/e2e/helpers/clear-database.ts) so starter
// talks/contacts don't collide with the titles and counts that ordinary specs assert on.
export const STARTER_CONTACTS_DISABLED_KEY = 'iinpublic_starter_contacts_disabled';
const lang = (locale?: UiLanguage): UiLanguage => locale === 'zh' ? 'zh' : 'en';

function makeTalk(contactId: StarterPracticeContactId, locale: UiLanguage, draft: any): any {
  const definition = definitions.find((item) => item.id === contactId)!;
  const talk = {
    ...draft,
    authorId: definition.userId,
    authorName: contactName(contactId, locale),
    isAdult: false,
    language: locale,
    tags: [],
    createdAt: '2026-01-01T00:00:00.000Z',
    isTemplate: false,
    usageCount: 0,
    starterContactId: contactId,
  };
  return { ...talk, id: computeTalkIdFromTalkData(talk) };
}

function contactName(id: StarterPracticeContactId, locale: UiLanguage): string {
  const zh = locale === 'zh';
  const names: Record<StarterPracticeContactId, [string, string]> = {
    'tag-guide': ['Tag Guide', '标签向导'],
    'flow-guide': ['Flow Guide', '流程向导'],
    'survey-guide': ['Survey Guide', '问卷向导'],
    'route-guide': ['Route Guide', '路线向导'],
  };
  return names[id][zh ? 1 : 0];
}

function contactDescription(id: StarterPracticeContactId, locale: UiLanguage): string {
  const zh = locale === 'zh';
  const descriptions: Record<StarterPracticeContactId, [string, string]> = {
    'tag-guide': ['Sends simple and paired Tag Talks', '发送简单标签和成对标签话题'],
    'flow-guide': ['Sends step-by-step Flow Talks', '发送分步流程话题'],
    'survey-guide': ['Sends Survey Talks', '发送问卷话题'],
    'route-guide': ['Sends branching Route Talks', '发送分支路线话题'],
  };
  return descriptions[id][zh ? 1 : 0];
}

function buildTalks(id: StarterPracticeContactId, locale: UiLanguage): any[] {
  const zh = locale === 'zh';
  if (id === 'tag-guide') {
    const tag = (title: string, accepted = title) => makeTalk(id, locale, {
      type: 'tag', title,
      questions: [{
        id: 'q_0', text: title,
        ...(accepted === title ? { tagKind: 'simple' } : { reciprocalTagContext: true }),
        answers: [
          { id: 'q_0_match', text: accepted, isMatch: true, isTerminal: true },
          { id: 'q_0_ignore', text: zh ? '忽略' : 'Ignore', isIgnore: true, isTerminal: true },
        ],
      }],
    });
    return [
      tag(zh ? '周末徒步' : 'Weekend hiking'),
      tag(zh ? '语言交换' : 'Language exchange'),
      tag(zh ? '我可以提供指导' : 'I can offer mentoring', zh ? '我想找一位导师' : 'I am looking for a mentor'),
      tag(zh ? '我正在招聘' : 'I am hiring', zh ? '我正在找工作' : 'I am looking for work'),
    ];
  }
  if (id === 'flow-guide') return [makeTalk(id, locale, {
    type: 'flow', title: zh ? '一起喝咖啡吗？' : 'Meet for coffee?',
    questions: [
      { id: 'q_0', text: zh ? '你想喝咖啡聊聊吗？' : 'Would you like to meet for coffee?', answers: [
        { id: 'yes', text: zh ? '想' : 'Yes', nextQuestionId: 'q_1' },
        { id: 'no', text: zh ? '暂时不' : 'Not now', isIgnore: true, isTerminal: true },
      ] },
      { id: 'q_1', text: zh ? '哪天方便？' : 'Which day works?', answers: [
        { id: 'weekend', text: zh ? '这个周末' : 'This weekend', isMatch: true, isTerminal: true },
        { id: 'skip', text: zh ? '暂时不' : 'Not now', isIgnore: true, isTerminal: true },
      ] },
    ],
  })];
  if (id === 'survey-guide') return [makeTalk(id, locale, {
    type: 'survey', title: zh ? '你更喜欢哪种活动？' : 'Which activity do you prefer?',
    questions: [{ id: 'q_0', text: zh ? '选一个你更喜欢的活动' : 'Pick the activity you prefer', isAggregatable: true, answers: [
      { id: 'outdoors', text: zh ? '户外活动' : 'Outdoors', isTerminal: true, counter: 0 },
      { id: 'games', text: zh ? '桌游' : 'Board games', isTerminal: true, counter: 0 },
      { id: 'food', text: zh ? '美食' : 'Trying food', isTerminal: true, counter: 0 },
      { id: 'skip', text: zh ? '跳过' : 'Skip', isIgnore: true, isTerminal: true },
    ] }],
  })];
  return [makeTalk(id, locale, {
    type: 'route', title: zh ? '找到适合的活动' : 'Find an activity that fits',
    questions: [
      { id: 'q_0', text: zh ? '你想室内还是户外？' : 'Indoors or outdoors?', contextPath: [], answers: [
        { id: 'indoors', text: zh ? '室内' : 'Indoors', nextQuestionId: 'q_1' },
        { id: 'outdoors', text: zh ? '户外' : 'Outdoors', nextQuestionId: 'q_2' },
        { id: 'skip', text: zh ? '跳过' : 'Skip', isIgnore: true, isTerminal: true },
      ] },
      { id: 'q_1', text: zh ? '一起玩桌游吗？' : 'How about board games?', contextPath: [{ questionId: 'q_0', answerId: 'indoors' }], answers: [
        { id: 'yes', text: zh ? '好' : 'Sounds good', isMatch: true, isTerminal: true },
        { id: 'no', text: zh ? '不合适' : 'Not for me', isIgnore: true, isTerminal: true },
      ] },
      { id: 'q_2', text: zh ? '一起徒步吗？' : 'How about a hike?', contextPath: [{ questionId: 'q_0', answerId: 'outdoors' }], answers: [
        { id: 'yes', text: zh ? '好' : 'Sounds good', isMatch: true, isTerminal: true },
        { id: 'no', text: zh ? '不合适' : 'Not for me', isIgnore: true, isTerminal: true },
      ] },
    ],
  })];
}

function readActiveIds(userId: string, eligible: boolean, storage: Storage): StarterPracticeContactId[] {
  if (!userId) return [];
  try {
    if (storage.getItem(STARTER_CONTACTS_DISABLED_KEY) === 'true') return [];
    const raw = storage.getItem(stateKey(userId));
    if (raw) {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed?.activeIds)
        ? parsed.activeIds.filter((id: string) => definitions.some((item) => item.id === id))
        : [];
    }
    if (!eligible) return [];
    const activeIds = definitions.map((item) => item.id);
    storage.setItem(stateKey(userId), JSON.stringify({ activeIds }));
    return activeIds;
  } catch {
    return eligible ? definitions.map((item) => item.id) : [];
  }
}

export function isStarterContactEligible(
  myTalks: MyTalkMap,
  conversations: Record<string, any>,
  knownPeople: KnownPerson[],
): boolean {
  const realConversations = Object.values(conversations).filter((conversation: any) => conversation?.supportChannel !== true);
  return Object.keys(myTalks).length === 0
    && realConversations.length === 0
    && knownPeople.length === 0;
}

export function getStarterPracticeContacts(
  userId: string,
  locale: UiLanguage,
  eligible: boolean,
  storage: Storage = settingsStorageAsStorage,
): StarterPracticeContact[] {
  const normalizedLocale = lang(locale);
  const activeIds = new Set(readActiveIds(userId, eligible, storage));
  return definitions
    .filter((definition) => activeIds.has(definition.id))
    .map((definition) => ({
      ...definition,
      name: contactName(definition.id, normalizedLocale),
      description: contactDescription(definition.id, normalizedLocale),
      talks: buildTalks(definition.id, normalizedLocale),
    }));
}

export function removeStarterPracticeContact(
  userId: string,
  id: StarterPracticeContactId,
  storage: Storage = settingsStorageAsStorage,
): void {
  const activeIds = readActiveIds(userId, false, storage).filter((candidate) => candidate !== id);
  storage.setItem(stateKey(userId), JSON.stringify({ activeIds }));
}

export function starterIncomingTalkClusters(contacts: StarterPracticeContact[]): any[] {
  return contacts.flatMap((contact) => contact.talks.map((talk, index) => ({
    identityKey: talk.id,
    latestTalkId: talk.id,
    title: talk.title,
    type: talk.type,
    language: talk.language,
    updatedAt: new Date(Date.UTC(2026, 0, 1, 0, index)).toISOString(),
    isAnswered: false,
    latestTalk: talk,
    senders: {
      [contact.userId]: {
        senderId: contact.userId,
        senderName: contact.name,
        lastTalkId: talk.id,
        starterContactId: contact.id,
      },
    },
    talkIds: { [talk.id]: talk.createdAt },
    starterContactId: contact.id,
    // Right after the starter tags and the "Create my tag" prompt: examples of making one's own.
    ...(contact.id === 'tag-guide' ? { starterGroup: tagGuideHeading(contact.talks[0]?.language) } : {}),
  })));
}

function tagGuideHeading(locale: UiLanguage | undefined): { id: string; icon: string; label: string } {
  return {
    id: 'tag-guide',
    icon: '🏷️',
    label: locale === 'zh' ? '标签向导' : 'Tag Guide',
  };
}

const interestStateKey = (userId: string): string =>
  `iinpublic_starter_interests_v${STARTER_TAGS_VERSION}:${userId}`;

/**
 * Starter interest tags (OPEN-87) for this identity, as incoming tag clusters from the local starter
 * source. Enrollment follows the demo contacts (a new identity with no real Talks, contacts or
 * conversations) and is remembered per user, so answering a tag does not make the rest disappear.
 * They sort above the demo contacts' Talks: newer `updatedAt`, catalog order kept (first = newest).
 */
export function starterInterestClusters(
  userId: string,
  locale: UiLanguage,
  eligible: boolean,
  storage: Storage = settingsStorageAsStorage,
): any[] {
  if (!userId) return [];
  try {
    if (storage.getItem(STARTER_CONTACTS_DISABLED_KEY) === 'true') return [];
    if (storage.getItem(interestStateKey(userId)) !== 'enrolled') {
      if (!eligible) return [];
      storage.setItem(interestStateKey(userId), 'enrolled');
    }
  } catch {
    if (!eligible) return [];
  }
  // One set per UI language (content written for that audience); groups are headings only.
  const newest = Date.UTC(2026, 0, 2);
  return starterTagsFor(lang(locale)).map((tag, index) => {
    const talk = starterTagTalk(tag);
    return {
      identityKey: talk.id,
      latestTalkId: talk.id,
      title: talk.title,
      type: talk.type,
      language: talk.language,
      updatedAt: new Date(newest - index * 60_000).toISOString(),
      isAnswered: false,
      latestTalk: talk,
      senders: {
        [STARTER_TAGS_SOURCE_ID]: {
          senderId: STARTER_TAGS_SOURCE_ID,
          senderName: 'IinPublic',
          lastTalkId: talk.id,
          starterContactId: STARTER_TAGS_CONTACT_ID,
        },
      },
      talkIds: { [talk.id]: talk.createdAt },
      starterContactId: STARTER_TAGS_CONTACT_ID,
      starterTagId: tag.id,
      starterGroup: { id: tag.group.id, icon: tag.group.icon, label: tag.group.label },
    };
  });
}

/**
 * The local starter seeds for one identity: the demo contacts and every starter cluster for the
 * incoming list (interest tags first, then the demo Talks). Enrollment needs a new identity with no
 * real Talks, contacts or conversations; once enrolled it is remembered per user.
 */
export function starterSeedsFor(
  userId: string,
  locale: UiLanguage,
  state: { myTalks: MyTalkMap; conversations: Record<string, any>; knownPeople: KnownPerson[]; incoming?: any[] },
): { contacts: StarterPracticeContact[]; clusters: any[] } {
  const eligible = isStarterContactEligible(state.myTalks, state.conversations, state.knownPeople);
  const contacts = getStarterPracticeContacts(userId, locale, eligible);
  // Once a real person has sent the same tag, theirs is the row: answering it must reach them.
  const sentByPeople = new Set((state.incoming || []).map((cluster) => String(cluster?.identityKey || '')));
  const interests = starterInterestClusters(userId, locale, eligible).filter((cluster) => !sentByPeople.has(cluster.identityKey));
  return { contacts, clusters: [...interests, ...starterIncomingTalkClusters(contacts)] };
}

/** The starter Talk a list row stands for, by talk id or identity key. */
export function findStarterTalk(clusters: any[], talkId: string, identityKeyFallback?: string): any | null {
  const cluster = clusters.find((candidate) =>
    String(candidate?.latestTalkId || '') === String(talkId || '')
    || String(candidate?.identityKey || '') === String(identityKeyFallback || ''));
  return cluster?.latestTalk || null;
}

/**
 * After a starter interest tag is answered with the normal tag logic: a Like's copy in My Talks
 * becomes the user's own Talk (no starter author or marker, same content and identity key) so it is
 * delivered to Nearby and answered like any Talk.
 */
export function adoptLikedStarterTag(talk: any, author: { id: string; name: string }): void {
  if (!isStarterTagTalk(talk) || !author.id) return;
  const entry = getMyTalks()[talk.id];
  if (!entry?.fullTalk) return;
  patchMyTalk(talk.id, { fullTalk: detachStarterTagTalk(entry.fullTalk, author) });
}
